import { useState, useEffect } from "react";
import { useProfileDraft, type WorkspaceTab } from "@/context/ProfileDraftContext";
import { trpc } from "@/lib/trpc";
import { PortfolioOutline } from "./PortfolioOutline";
import { MainInspector } from "./MainInspector";
import { WorkspaceToolbar } from "./WorkspaceToolbar";
import { LivePreviewPanel } from "@/components/preview/LivePreviewPanel";
import { usePreviewHtml } from "@/lib/editor/use-preview-html";
import type { SectionType } from "@profolio/validators";
import {
  Home,
  Eye,
  EyeOff,
  X,
  Layers,
} from "lucide-react";

export function EditorShell() {
  const {
    draft,
    activeTab,
    requestNavigation,
    activeSectionId,
    previewOpen,
    setPreviewOpen,
    userTier,
    effectivePreviewDraft,
    onNavigateToSection,
    onUpgradeClick,
    dispatch,
  } = useProfileDraft();

  // Preview Drawer Resizing
  const [previewWidth, setPreviewWidth] = useState<number>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("profolio_preview_width");
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && parsed >= 340 && parsed <= 900) return parsed;
      }
    }
    return 540;
  });

  const [isDraggingPreview, setIsDraggingPreview] = useState(false);

  const handleStartResize = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingPreview(true);

    const startX = e.clientX;
    const startWidth = previewWidth;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      const newWidth = Math.max(340, Math.min(900, startWidth + delta));
      setPreviewWidth(newWidth);
    };

    const onMouseUp = () => {
      setIsDraggingPreview(false);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      localStorage.setItem("profolio_preview_width", String(previewWidth));
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  // Auto-collapse preview on Overview, Pages, or Publish to allow full width breathing room
  useEffect(() => {
    if (activeTab === "home" || activeTab === "pages" || activeTab === "publish") {
      // Auto-collapse preview on non-visual canvas tabs if on smaller viewports
      if (typeof window !== "undefined" && window.innerWidth < 1440) {
        setPreviewOpen(false);
      }
    }
  }, [activeTab, setPreviewOpen]);

  // Multi-page preview support
  const { data: pagesData } = trpc.pages.listPages.useQuery(undefined, {
    staleTime: 5000,
  });
  const [activePreviewSlug, setActivePreviewSlug] = useState<string>("");

  // Derive preview HTML from effective draft with multi-page support
  const previewHtml = usePreviewHtml(effectivePreviewDraft, {
    pages: pagesData,
    activeSlug: activePreviewSlug,
  });

  const activeSection = draft?.sections.find((s) => s.id === activeSectionId);

  // Mobile More Sheet State
  const [isMobileMoreOpen, setIsMobileMoreOpen] = useState(false);

  // Sidebar Pin State
  const [isSidebarPinned, setIsSidebarPinned] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("profolio_sidebar_pinned");
      return saved !== "false";
    }
    return true;
  });

  const handleToggleSidebar = () => {
    const next = !isSidebarPinned;
    setIsSidebarPinned(next);
    localStorage.setItem("profolio_sidebar_pinned", String(next));
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background text-foreground">
      {/* ── 1. Portfolio Outline (Persistent Left Sidebar) ── */}
      <div className={`hidden md:block relative z-30 transition-all duration-300 ease-in-out shrink-0 ${isSidebarPinned ? 'w-64' : 'w-14'}`}>
        <aside 
          className={`absolute inset-y-0 left-0 bg-card border-r border-border flex flex-col transition-all duration-300 ease-in-out overflow-hidden z-30 group/sidebar ${isSidebarPinned ? 'w-64' : 'w-14 hover:w-64 hover:shadow-2xl'}`}
        >
          <div className={`${isSidebarPinned ? 'w-64' : 'w-14 group-hover/sidebar:w-64'} h-full flex flex-col transition-all duration-300 ease-in-out`}>
            <PortfolioOutline isPinned={isSidebarPinned} onTogglePin={handleToggleSidebar} />
          </div>
        </aside>
      </div>

      {/* ── 2. Primary Editor Column (Toolbar + Main Inspector) ── */}
      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        <WorkspaceToolbar onTogglePreview={() => setPreviewOpen(!previewOpen)} />

        <div className="flex flex-1 overflow-hidden relative">
          {/* Center: Main Inspector */}
          <div className="flex-1 overflow-hidden flex flex-col min-w-0 bg-background">
            <MainInspector />
          </div>

          {/* ── 3. Docked Resizable Live Preview Panel ── */}
          {previewOpen && (
            <aside
              aria-label="Live Site Preview"
              style={{ width: `${previewWidth}px` }}
              className="relative border-l border-border bg-background shrink-0 hidden md:flex flex-col h-full shadow-lg z-20 transition-[width] duration-75 ease-out"
            >
              {/* Draggable Resize Handle on Left Border */}
              <div
                onMouseDown={handleStartResize}
                className="group absolute -left-2 top-0 bottom-0 w-4 cursor-col-resize z-30 flex items-center justify-center hover:bg-primary/20 transition-colors"
                title="Drag to resize preview drawer"
              >
                <div className="w-1 h-8 rounded-full bg-border/80 group-hover:bg-primary transition-colors" />
              </div>

              <LivePreviewPanel
                html={previewHtml}
                isOpen={previewOpen}
                onToggle={() => setPreviewOpen(false)}
                activeSectionType={activeSection?.type}
                onSectionClick={(secType) => onNavigateToSection(secType as SectionType)}
                profile={effectivePreviewDraft?.profile || draft?.profile}
                userTier={userTier}
                onUpgradeClick={() => onUpgradeClick()}
                isDragging={isDraggingPreview}
                pages={pagesData}
                activeSlug={activePreviewSlug}
                onNavigateSlug={(slug) => setActivePreviewSlug(slug)}
                onUpdateSeo={(seoOverrides) =>
                  dispatch({ type: "UPDATE_PROFILE", payload: { seoOverrides } })
                }
              />
            </aside>
          )}

          {/* Mobile Full-Screen Preview Modal */}
          {previewOpen && (
            <div className="md:hidden fixed inset-0 z-50 flex flex-col bg-background animate-in fade-in duration-150">
              <div className="flex items-center justify-between border-b border-border px-4 py-2 bg-card">
                <span className="text-xs font-semibold text-foreground">Mobile Live Preview</span>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(false)}
                  aria-label="Close mobile preview"
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                <LivePreviewPanel
                  html={previewHtml}
                  isOpen={previewOpen}
                  onToggle={() => setPreviewOpen(false)}
                  activeSectionType={activeSection?.type}
                  onSectionClick={(secType) => {
                    setPreviewOpen(false);
                    onNavigateToSection(secType as SectionType);
                  }}
                  profile={effectivePreviewDraft?.profile || draft?.profile}
                  userTier={userTier}
                  onUpgradeClick={() => onUpgradeClick()}
                  pages={pagesData}
                  activeSlug={activePreviewSlug}
                  onNavigateSlug={(slug) => setActivePreviewSlug(slug)}
                  onUpdateSeo={(seoOverrides) =>
                    dispatch({ type: "UPDATE_PROFILE", payload: { seoOverrides } })
                  }
                />
              </div>
            </div>
          )}
        </div>

        {/* ── 4. Mobile Bottom Navigation Bar (< 768px) ── */}
        <nav
          aria-label="Mobile Navigation"
          className="md:hidden flex items-center justify-around border-t border-border bg-card/90 backdrop-blur-md px-1 py-1 shrink-0 z-40"
        >
          {[
            { id: "home" as WorkspaceTab, label: "Overview", icon: Home },
          ].map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() =>
                  requestNavigation({
                    destination: item.id,
                    destinationType: "workspace",
                    source: "mobile_bottom_nav",
                  })
                }
                className={`flex flex-col items-center justify-center min-w-[56px] py-1.5 px-2 rounded-lg text-[10px] font-medium transition-colors cursor-pointer ${
                  isActive
                    ? "text-primary font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className={`h-5 w-5 mb-0.5 ${isActive ? "text-primary" : ""}`} />
                <span>{item.label}</span>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => setPreviewOpen(!previewOpen)}
            className={`flex flex-col items-center justify-center min-w-[56px] py-1.5 px-2 rounded-lg text-[10px] font-medium transition-colors cursor-pointer ${
              previewOpen
                ? "text-primary font-semibold"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {previewOpen ? <EyeOff className="h-5 w-5 mb-0.5 text-primary" /> : <Eye className="h-5 w-5 mb-0.5" />}
            <span>Preview</span>
          </button>

          {/* Mobile "Outline" Button */}
          <button
            type="button"
            onClick={() => setIsMobileMoreOpen(true)}
            className="flex flex-col items-center justify-center min-w-[56px] py-1.5 px-2 rounded-lg text-[10px] font-medium text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <Layers className="h-5 w-5 mb-0.5" />
            <span>Outline</span>
          </button>
        </nav>
      </div>

      {/* ── 5. Mobile "Outline" Sheet ── */}
      {isMobileMoreOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-background flex flex-col animate-in fade-in duration-150 pb-16">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <span className="text-sm font-bold text-foreground">Portfolio Outline</span>
            <button
              type="button"
              onClick={() => setIsMobileMoreOpen(false)}
              className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <PortfolioOutline onNavigate={() => setIsMobileMoreOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
